import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { trpc } from '@/config/trpc'
import { toast } from 'sonner'
import { AuthVerificationError } from '@/components/auth/auth-verification/auth-verification-error'
import { usePostHog } from 'posthog-js/react'

export function AuthUserPassVerifyPage() {
  const navigate = useNavigate()
  const posthog = usePostHog()
  const [searchParams] = useSearchParams()

  const [verifyError, setVerifyError] = useState<string>()

  const { mutateAsync: verifyUser } = trpc.auth.verifyUser.useMutation()

  const isMutationRunning = useRef(false)

  // Get params from URL
  const token = searchParams.get('token')

  useEffect(() => {
    if (!token) return

    if (isMutationRunning.current) {
      console.debug('Mutation already in progress, skipping duplicate call')
      console.debug('This should only happein in development due to StrictMode')
      return
    }

    isMutationRunning.current = true
    posthog.capture('auth_verification_link_clicked')

    const onSuccess = () => {
      toast.success('Account verified successfully', {
        description: 'You can now log in',
      })
      navigate('/login')
    }
    const onError = (error: unknown) => {
      console.error('Error verifying user:', error)
      const message = error instanceof Error ? error.message : String(error)
      const reason = message.startsWith('Token has expired')
        ? 'token_expired'
        : message === 'Invalid token'
          ? 'invalid_token'
          : 'unknown'
      posthog.capture('auth_verification_failed', { reason })
      setVerifyError(message)
    }

    verifyUser({ verifyToken: token })
      .then(() => onSuccess())
      .catch(onError)
      .finally(() => {
        isMutationRunning.current = false
      })
  }, [token, verifyUser, navigate, posthog])

  if (verifyError) {
    return (
      <div className="bg-background flex h-screen w-full items-center justify-center">
        <div className="mx-auto flex max-w-md flex-1 flex-col justify-center">
          <div className="shadow-xs border-border bg-card relative mb-16 overflow-hidden rounded-md border p-6">
            <div className="relative z-10">
              <AuthVerificationError message={verifyError} />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="border-primary/30 border-t-primary h-12 w-12 animate-spin rounded-full border-4"></div>
        <p className="text-muted-foreground">Verifying your account...</p>
      </div>
    </div>
  )
}
